/**
 * Reglas de identidad de personas y organizaciones, en un solo lugar.
 * Las consumen los formularios de la PWA, el formulario público y las funciones de servidor:
 * la validación del cliente es para la experiencia, la del servidor es la que manda.
 */
import { TIPOS_DOCUMENTO_DIAN, type ContextoDocumento } from './catalogos-dian';
import { validarDocumento } from './validar-documento';

type CodigoError =
  | 'NOMBRES_REQUERIDOS'
  | 'APELLIDOS_REQUERIDOS'
  | 'RAZON_SOCIAL_REQUERIDA'
  | 'TIPO_DOCUMENTO_INVALIDO'
  | 'DOCUMENTO_INVALIDO'
  | 'TELEFONO_INVALIDO'
  | 'CELULAR_REQUERIDO'
  | 'CORREO_INVALIDO'
  | 'CORREO_REQUERIDO'
  | 'DOCUMENTO_DUPLICADO'
  | 'CONSENTIMIENTO_REQUERIDO';

/** Un solo texto por error, para que los cuatro formularios digan lo mismo. */
const MENSAJES: Record<CodigoError, string> = {
  NOMBRES_REQUERIDOS: 'Escribe el nombre.',
  APELLIDOS_REQUERIDOS: 'Escribe el apellido.',
  RAZON_SOCIAL_REQUERIDA: 'Escribe la razón social.',
  TIPO_DOCUMENTO_INVALIDO: 'Elige un tipo de documento válido.',
  DOCUMENTO_INVALIDO: 'El número de documento no corresponde al tipo elegido.',
  TELEFONO_INVALIDO: 'El celular debe tener 10 dígitos.',
  CELULAR_REQUERIDO: 'Escribe el celular.',
  CORREO_INVALIDO: 'El correo no es válido.',
  CORREO_REQUERIDO: 'Escribe el correo.',
  DOCUMENTO_DUPLICADO: 'Ya existe un registro con ese documento.',
  CONSENTIMIENTO_REQUERIDO: 'Falta la autorización de tratamiento de datos.',
};

export function mensajeDe(codigo: CodigoError | null): string {
  return codigo ? MENSAJES[codigo] : '';
}

const REGEX_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Teléfono colombiano a E.164. Devuelve null si no quedan 10 dígitos nacionales,
 * que es lo que tiene un celular en Colombia.
 */
export function normalizarTelefonoCO(entrada: string): string | null {
  const digitos = (entrada ?? '').replace(/\D/g, '').replace(/^0+/, '');
  const nacional = digitos.length === 12 && digitos.startsWith('57') ? digitos.slice(2) : digitos;
  if (nacional.length !== 10) return null;
  return '+57' + nacional;
}

/** Los tipos de documento que aplican a un contexto (un cliente no se identifica con un NIT de empresa). */
export function tiposDocumentoDe(contexto: ContextoDocumento) {
  return TIPOS_DOCUMENTO_DIAN.filter((t) => t.contextos.includes(contexto));
}

export interface DatosTercero {
  tipoDocumento: string;
  numeroDocumento: string;
  nombres: string;
  apellidos?: string;
  razonSocial?: string;
  telefono?: string;
  correo?: string;
  contexto: ContextoDocumento;
  autorizaDatos?: boolean;
  exigeConsentimiento?: boolean;
}

/**
 * Valida una persona o empresa. Devuelve el código del primer problema, o null si está bien.
 * El NIT (31) identifica a una organización: exige razón social y no pide apellidos.
 */
export function validarTercero(d: DatosTercero): CodigoError | null {
  const esOrganizacion = d.tipoDocumento === '31';

  if (!tiposDocumentoDe(d.contexto).some((t) => t.codigo === d.tipoDocumento)) {
    return 'TIPO_DOCUMENTO_INVALIDO';
  }
  if (!validarDocumento(d.tipoDocumento, d.numeroDocumento ?? '')) return 'DOCUMENTO_INVALIDO';

  if (esOrganizacion) {
    if ((d.razonSocial ?? '').trim().length < 3) return 'RAZON_SOCIAL_REQUERIDA';
  } else {
    if ((d.nombres ?? '').trim().length < 2) return 'NOMBRES_REQUERIDOS';
    if ((d.apellidos ?? '').trim().length < 2) return 'APELLIDOS_REQUERIDOS';
  }

  // Celular y correo son los dos canales para contactar al cliente sobre sus compras, que es lo que
  // autoriza el consentimiento de habeas data: ninguno es opcional.
  if (!(d.telefono ?? '').trim()) return 'CELULAR_REQUERIDO';
  if (normalizarTelefonoCO(d.telefono!) === null) return 'TELEFONO_INVALIDO';
  if (!(d.correo ?? '').trim()) return 'CORREO_REQUERIDO';
  if (!REGEX_CORREO.test(d.correo!.trim())) return 'CORREO_INVALIDO';
  if (d.exigeConsentimiento && !d.autorizaDatos) return 'CONSENTIMIENTO_REQUERIDO';

  return null;
}
